import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../../../assets/contexts/ThemeContext';
import { usePageLayout } from '../../../../assets/contexts/PageLayoutContext';
import CustomSelect from '../../../../components/widgets/CustomSelect';
import Http from '../../../../core/Http';
import {
  flattenFooterDataForAreaManagerEditor,
  shouldLoadFooterPartnerLogoImageUrl
} from '../../../../assets/lib/footer/footerContentNormalize';
import { DEFAULT_RELATED_SITES, normalizeRelatedSitesPayload } from '../../../../assets/constants/relatedSitesDefault';

/** 페이지 전용 스타일 (기존 FooterAreaManager.css 이관). CustomSelect·아이콘 버튼·링크 구분선 등 */
const FOOTER_AREA_MANAGER_PAGE_CSS = `
.footer-area-manager-page .custom-select__control {
  min-height: 30px !important;
  border-radius: 4px !important;
  font-size: 0.875rem !important;
  border-color: var(--bs-border-color, #ced4da);
}
.footer-area-manager-page .custom-select__control:hover {
  border-color: var(--bs-border-color-translucent, #adb5bd);
}
.footer-area-manager-page .custom-select__control--is-focused {
  border-color: #86b7fe !important;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25) !important;
}
.footer-area-manager-page .custom-select__value-container {
  padding: 0 6px 0 8px !important;
  font-size: 0.875rem !important;
}
.footer-area-manager-page .custom-select__input-container {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 0.875rem !important;
}
.footer-area-manager-page .custom-select__single-value {
  font-size: 0.875rem !important;
}
.footer-area-manager-page .custom-select__indicators {
  height: 30px !important;
}
.footer-area-manager-page .custom-select__indicator {
  padding: 4px 6px !important;
}
.footer-area-manager-page .custom-select__dropdown-indicator svg {
  width: 12px !important;
  height: 12px !important;
}
.footer-area-manager-page .custom-select__placeholder {
  font-size: 0.875rem !important;
}
.footer-area-manager-page .social-link-type-select {
  width: 130px !important;
  min-width: 130px !important;
  max-width: 130px !important;
  flex-shrink: 0;
}
.footer-area-manager-page .social-link-type-select .custom-select__control {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}
.footer-area-manager-page .social-link-type-select .custom-select__value-container {
  overflow: hidden;
}
.footer-area-manager-page .social-link-type-select .custom-select__single-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.footer-area-manager-page .btn-icon-square {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  min-height: 28px !important;
  max-height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-sizing: border-box;
  line-height: 1 !important;
  flex-shrink: 0;
  flex-grow: 0;
}
.footer-area-manager-page .btn-icon-square svg {
  flex-shrink: 0;
}
.footer-area-manager-page .btn-icon-square.rounded-circle {
  border-radius: 50% !important;
}
.footer-area-manager-page .footer-link-item-divider {
  margin: 1rem 0;
  border: 0;
  border-top: 1px solid var(--bs-border-color, #dee2e6);
  opacity: 1;
}
`.trim();

const FOOTER_THEME_OPTIONS = [
  { value: 'simple', label: 'Simple' },
  { value: 'modern', label: 'Modern' },
  { value: 'vibrant', label: 'Vibrant' },
  { value: 'glow', label: 'GLOW' }
];

/** 편집기 초기·조회 실패 시 사용. DB에 저장된 값만 채우고, 샘플 폴백은 두지 않음 */
const emptyCopyrightForEditor = () => ({
  copyright: { kor: '', eng: '' },
  links: []
});

/** CSS `.footer-area.footer-theme-glow` 기본 배경과 동일 — 미리보기·칼라 피커 기본값 */
const GLOW_FOOTER_DEFAULT_THEME_BG = '#002d56';

function resolveGlowFooterPreviewBackground(glowPlatform) {
  const v = glowPlatform?.themeBackgroundColor;
  if (v != null && String(v).trim() !== '') return String(v).trim();
  return GLOW_FOOTER_DEFAULT_THEME_BG;
}

const getEmptyFooterAreaEditorData = () => ({
  glowPlatform: {
    themeBackgroundColor: '',
    logo: { size: 49, backgroundColor: '', border: '', letter: '', fontSize: '' },
    title: '',
    titleEn: '',
    subtitle: '',
    subtitleEn: '',
    description: '',
    descriptionEn: '',
    badges: [],
    partnerLogos: []
  },
  linkSections: [],
  contact: { title: '', titleEn: '', items: [] },
  copyright: emptyCopyrightForEditor()
});

const LINK_SECTION_ICON_OPTIONS = [
  { value: 'arrow', label: 'arrow' },
  { value: 'external', label: 'external' }
];

const BORDER_STYLE_OPTIONS = [
  { value: 'solid', label: 'solid' },
  { value: 'dashed', label: 'dashed' },
  { value: 'none', label: 'none' }
];

const CONTACT_ICON_TYPES = [
  { value: 'address', label: '주소 (map-pin)' },
  { value: 'phone', label: '전화 (phone)' },
  { value: 'E-mail', label: '이메일 (mail)' },
  { value: 'hours', label: '운영시간 (clock)' },
  { value: 'other', label: '기타' }
];

function getDefaultCopyright() {
  return {
    copyright: {
      kor: 'Copyright @ RISE Center, Hankuk University of Foreign Studies. All Rights Reserved.',
      eng: 'Copyright @ RISE Center, Hankuk University of Foreign Studies. All Rights Reserved.'
    },
    links: [
      { kor: '개인정보처리방침', eng: 'Privacy Policy', link: '/privacy' },
      { kor: '이용약관', eng: 'Terms of Use', link: '/terms' },
      { kor: '사이트맵', eng: 'Sitemap', link: '/sitemap' }
    ],
    poweredBy: {
      kor: 'Powered by GLOW Platform & HyperCLOVA X',
      eng: 'Powered by GLOW Platform & HyperCLOVA X'
    }
  };
}

// 문의 & 지원 미리보기용 아이콘 (푸터와 동일한 Lucide 윤곽선 스타일, #51a2ff)
const CONTACT_PREVIEW_ICON_STYLE = { color: '#51a2ff' };
const CONTACT_PREVIEW_ICONS = {
  address: (
    <svg style={CONTACT_PREVIEW_ICON_STYLE} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-map-pin flex-shrink-0" aria-hidden="true">
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  phone: (
    <svg style={CONTACT_PREVIEW_ICON_STYLE} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-phone flex-shrink-0" aria-hidden="true">
      <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
    </svg>
  ),
  'E-mail': (
    <svg style={CONTACT_PREVIEW_ICON_STYLE} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mail flex-shrink-0" aria-hidden="true">
      <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
      <rect x="2" y="4" width="20" height="16" rx="2" />
    </svg>
  ),
  hours: (
    <svg style={CONTACT_PREVIEW_ICON_STYLE} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clock flex-shrink-0" aria-hidden="true">
      <path d="M12 6v6l4 2" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  )
};

// 삭제 버튼용 X 아이콘 (Lucide 스타일)
const DeleteIconX = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

// 추가 버튼용 + 아이콘 (Lucide 스타일)
const AddIconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5v14" />
  </svg>
);

/**
 * Footer 영역 관리 페이지
 * footer-area 콘텐츠를 UI로 편집하고, 추후 DB 연동 시 저장하는 용도
 */
const FooterAreaManager = () => {
  const { setBannerData } = usePageLayout();
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || (typeof window !== 'undefined' && window.__themeConfig) || {};
  const setTheme = themeContext?.set;

  // 공통 제목 배너 설정
  useEffect(() => {
    setBannerData({
      title: 'Footer 영역 관리',
      breadcrumbPath: '시스템 관리 > Footer 영역 관리',
      loading: false
    });
    return () => setBannerData(null);
  }, [setBannerData]);

  // Footer 테마: ThemeConfig와 양방향 바인딩
  const [footerTheme, setFooterThemeState] = useState(themeConfig.footerTheme || 'simple');
  useEffect(() => {
    const dbTheme = typeof window !== 'undefined' && window.__themeConfig?.footerTheme;
    const next = dbTheme || themeConfig.footerTheme || 'simple';
    setFooterThemeState(next);
  }, [themeConfig.footerTheme]);

  const handleFooterThemeChange = (option) => {
    const value = option?.value ?? option;
    setFooterThemeState(value);
    if (setTheme) setTheme('footerTheme', value);
    if (typeof window !== 'undefined') {
      window.__themeConfig = { ...(window.__themeConfig || {}), footerTheme: value };
    }
    // 실제 .footer-area 클래스 적용
    const footerArea = document.querySelector('.footer-area');
    if (footerArea) {
      footerArea.classList.remove('footer-theme-simple', 'footer-theme-modern', 'footer-theme-vibrant', 'footer-theme-glow');
      footerArea.classList.add(`footer-theme-${value}`);
    }
  };

  const documentMetaRef = useRef({ schemaVersion: 1, description: null, footerId: null });

  const mergeLoadedFooterData = useCallback((loaded) => {
    const def = getEmptyFooterAreaEditorData();
    if (!loaded || typeof loaded !== 'object') {
      return flattenFooterDataForAreaManagerEditor(def);
    }
    const merged = {
      ...def,
      ...loaded,
      glowPlatform: {
        ...def.glowPlatform,
        ...(loaded.glowPlatform || {}),
        badges: Array.isArray(loaded.glowPlatform?.badges) ? loaded.glowPlatform.badges : def.glowPlatform.badges,
        partnerLogos: Array.isArray(loaded.glowPlatform?.partnerLogos) ? loaded.glowPlatform.partnerLogos : def.glowPlatform.partnerLogos
      },
      contact: {
        ...def.contact,
        ...(loaded.contact || {}),
        items: Array.isArray(loaded.contact?.items) ? loaded.contact.items : def.contact.items,
      },
      copyright: { ...def.copyright, ...(loaded.copyright || {}) },
      linkSections: Array.isArray(loaded.linkSections) ? loaded.linkSections : def.linkSections,
    };
    delete merged.statistics;
    delete merged.newsletter;
    if (merged.copyright) delete merged.copyright.poweredBy;
    if (merged.glowPlatform) delete merged.glowPlatform.socialMedia;
    return flattenFooterDataForAreaManagerEditor(merged);
  }, []);

  const [data, setData] = useState(() => getEmptyFooterAreaEditorData());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const [relatedSites, setRelatedSites] = useState(() => ({ ...DEFAULT_RELATED_SITES }));
  const [relatedSitesLoading, setRelatedSitesLoading] = useState(true);
  const [relatedSitesLoadError, setRelatedSitesLoadError] = useState(null);
  const [relatedSitesSaving, setRelatedSitesSaving] = useState(false);
  const [relatedSitesSaveMessage, setRelatedSitesSaveMessage] = useState(null);

  const updateData = useCallback((path, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        if (!(key in cur)) cur[key] = {};
        cur = cur[key];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  const workplaceId = Number(themeConfig.workspaceId || (typeof window !== 'undefined' && window.__themeConfig?.workspaceId)) || 1;

  useEffect(() => {
    let cancelled = false;
    const wid = workplaceId;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const row = await Http.get(`/api/system/footer-config/workspace/${wid}/latest`);
        if (cancelled) return;
        if (!row?.footerData) {
          setData(getEmptyFooterAreaEditorData());
          documentMetaRef.current = { schemaVersion: 1, description: null, footerId: row?.footerId ?? null };
          return;
        }
        const inner = typeof row.footerData === 'string' ? JSON.parse(row.footerData) : row.footerData;
        documentMetaRef.current = {
          schemaVersion: inner?.schemaVersion ?? 1,
          description: inner?.description ?? null,
          footerId: row.footerId ?? null,
        };
        if (inner?.footerTheme) {
          setFooterThemeState(inner.footerTheme);
          if (typeof window !== 'undefined') {
            window.__themeConfig = { ...(window.__themeConfig || {}), footerTheme: inner.footerTheme };
          }
          if (setTheme) setTheme('footerTheme', inner.footerTheme);
        }
        const fd = inner?.footerContent?.footerData;
        if (fd && typeof fd === 'object') {
          setData(mergeLoadedFooterData(fd));
        } else {
          setData(getEmptyFooterAreaEditorData());
        }
      } catch (e) {
        const status = e?.response?.status;
        if (!cancelled) {
          setData(getEmptyFooterAreaEditorData());
          documentMetaRef.current = { schemaVersion: 1, description: null, footerId: null };
        }
        if (status === 204 || status === 404) {
          if (!cancelled) setLoadError(null);
        } else if (!cancelled) {
          const detail = e?.response?.data?.message || e?.message || '';
          setLoadError(
            `workplace_footer_config 테이블에서 최신 푸터 설정을 읽지 못했습니다. (서버 오류 또는 DB 접근 실패 가능)${detail ? ` — ${detail}` : ''}`
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workplaceId, mergeLoadedFooterData, setTheme]);

  useEffect(() => {
    let cancelled = false;
    const wid = workplaceId;
    (async () => {
      setRelatedSitesLoading(true);
      setRelatedSitesLoadError(null);
      try {
        const row = await Http.get(`/api/system/related-sites-config/workspace/${wid}/latest`);
        if (cancelled) return;
        if (!row?.relatedSitesData) {
          setRelatedSites(normalizeRelatedSitesPayload(null));
          return;
        }
        const inner = typeof row.relatedSitesData === 'string' ? JSON.parse(row.relatedSitesData) : row.relatedSitesData;
        setRelatedSites(normalizeRelatedSitesPayload(inner));
      } catch (e) {
        const status = e?.response?.status;
        if (!cancelled) {
          setRelatedSites(normalizeRelatedSitesPayload(null));
        }
        if (status === 204 || status === 404) {
          if (!cancelled) setRelatedSitesLoadError(null);
        } else if (!cancelled) {
          const detail = e?.response?.data?.message || e?.message || '';
          setRelatedSitesLoadError(
            `workplace_related_sites_config 조회 실패${detail ? ` — ${detail}` : ''}`
          );
        }
      } finally {
        if (!cancelled) setRelatedSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workplaceId]);

  const addRelatedSiteRow = () => {
    setRelatedSites((prev) => ({
      ...prev,
      sites: [...(prev.sites || []), { image: '', url: '', name: '', nameEn: '' }]
    }));
  };

  const removeRelatedSiteRow = (index) => {
    setRelatedSites((prev) => ({
      ...prev,
      sites: (prev.sites || []).filter((_, i) => i !== index)
    }));
  };

  const setRelatedSiteField = (index, field, value) => {
    setRelatedSites((prev) => {
      const sites = [...(prev.sites || [])];
      while (sites.length <= index) sites.push({ image: '', url: '', name: '', nameEn: '' });
      sites[index] = { ...sites[index], [field]: value };
      return { ...prev, sites };
    });
  };

  const handleSaveRelatedSites = async () => {
    setRelatedSitesSaving(true);
    setRelatedSitesSaveMessage(null);
    try {
      const wid = workplaceId;
      const payload = normalizeRelatedSitesPayload(relatedSites);
      const relatedSitesDataJson = JSON.stringify(payload);
      await Http.post(`/api/system/related-sites-config/workspace/${wid}`, { relatedSitesData: relatedSitesDataJson });
      if (typeof window !== 'undefined') {
        window.relatedSites = payload;
        window.dispatchEvent(new CustomEvent('glow-related-sites-saved', { detail: { workplaceId: wid } }));
      }
      setRelatedSitesSaveMessage('협력 기관 설정이 저장되었습니다. 메인 화면 캐러셀에 반영됩니다.');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '저장 중 오류가 발생했습니다.';
      setRelatedSitesSaveMessage(msg);
    } finally {
      setRelatedSitesSaving(false);
    }
  };

  // GLOW Platform: 배지 행 추가/삭제
  const addBadge = () => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.glowPlatform) next.glowPlatform = { badges: [] };
      if (!next.glowPlatform.badges) next.glowPlatform.badges = [];
      next.glowPlatform.badges.push({ text: '', textEn: '', backgroundColor: '#065f46', borderColor: '#05df72', color: '#05df72', dotColor: '#4ade80' });
      return next;
    });
  };
  const removeBadge = (bi) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (next.glowPlatform?.badges) next.glowPlatform.badges = next.glowPlatform.badges.filter((_, i) => i !== bi);
      return next;
    });
  };

  const addPartnerLogo = () => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.glowPlatform) next.glowPlatform = { partnerLogos: [] };
      if (!Array.isArray(next.glowPlatform.partnerLogos)) next.glowPlatform.partnerLogos = [];
      const n = next.glowPlatform.partnerLogos.length;
      next.glowPlatform.partnerLogos.push({
        sortOrder: n + 1,
        imageUrl: '',
        link: '',
        alt: '',
        altEn: ''
      });
      return next;
    });
  };
  const removePartnerLogo = (pi) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (next.glowPlatform?.partnerLogos) {
        next.glowPlatform.partnerLogos = next.glowPlatform.partnerLogos.filter((_, i) => i !== pi);
      }
      return next;
    });
  };

  const addLinkSection = () => {
    setData((prev) => {
      const sections = prev.linkSections || [];
      const maxSo = sections.reduce((m, s) => {
        const v = Number(s.sortOrder);
        return Number.isFinite(v) ? Math.max(m, v) : m;
      }, 0);
      return {
        ...prev,
        linkSections: [
          ...sections,
          {
            sortOrder: maxSo + 1,
            title: '새 섹션',
            titleEn: '',
            iconType: 'arrow',
            items: [{ sortOrder: 1, text: '', textEn: '', link: '#', openInNewTab: false }]
          }
        ]
      };
    });
  };

  const removeLinkSection = (sectionIndex) => {
    setData((prev) => ({
      ...prev,
      linkSections: (prev.linkSections || []).filter((_, i) => i !== sectionIndex)
    }));
  };

  const addLinkItem = (sectionIndex) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const section = next.linkSections[sectionIndex];
      if (!section) return prev;
      section.items = section.items || [];
      const maxItem = section.items.reduce((m, x) => {
        const v = Number(x.sortOrder);
        return Number.isFinite(v) ? Math.max(m, v) : m;
      }, 0);
      section.items.push({ sortOrder: maxItem + 1, text: '', textEn: '', link: '#', openInNewTab: false });
      return next;
    });
  };

  const removeLinkItem = (sectionIndex, itemIndex) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const section = next.linkSections[sectionIndex];
      if (!section?.items) return prev;
      section.items = section.items.filter((_, i) => i !== itemIndex);
      return next;
    });
  };

  const setLinkSection = (sectionIndex, field, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.linkSections[sectionIndex]) return prev;
      next.linkSections[sectionIndex][field] = value;
      return next;
    });
  };

  const setLinkItem = (sectionIndex, itemIndex, field, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const section = next.linkSections[sectionIndex];
      if (!section?.items?.[itemIndex]) return prev;
      section.items[itemIndex][field] = value;
      return next;
    });
  };

  const addContactItem = () => {
    setData((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        items: [...(prev.contact?.items || []), { iconType: 'phone', type: 'phone', content: '', contentEn: '' }]
      }
    }));
  };

  const removeContactItem = (index) => {
    setData((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        items: (prev.contact?.items || []).filter((_, i) => i !== index)
      }
    }));
  };

  const setContactItem = (index, field, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.contact?.items?.[index]) return prev;
      next.contact.items[index][field] = value;
      return next;
    });
  };

  // 카피라이트 하단 링크: 행 추가/삭제
  const addFooterBottomLink = () => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.copyright) next.copyright = emptyCopyrightForEditor();
      next.copyright.links = [...(next.copyright.links ?? []), { kor: '', eng: '', link: '' }];
      return next;
    });
  };

  const removeFooterBottomLink = (index) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.copyright) next.copyright = emptyCopyrightForEditor();
      const links = next.copyright.links ?? [];
      next.copyright.links = links.filter((_, i) => i !== index);
      return next;
    });
  };

  const setFooterBottomLinkItem = (itemIndex, field, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.copyright) next.copyright = emptyCopyrightForEditor();
      const baseLinks = next.copyright.links ?? [];
      next.copyright.links = baseLinks.map((l) => ({ ...l }));
      while (next.copyright.links.length <= itemIndex) next.copyright.links.push({ kor: '', eng: '', link: '' });
      next.copyright.links[itemIndex] = { ...next.copyright.links[itemIndex], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const wid = workplaceId;
      const meta = documentMetaRef.current;
      const doc = {
        schemaVersion: meta.schemaVersion ?? 1,
        ...(meta.description ? { description: meta.description } : {}),
        footerTheme,
        footerContent: { footerData: data },
      };
      const footerDataJson = JSON.stringify(doc);
      await Http.post(`/api/system/footer-config/workspace/${wid}`, { footerData: footerDataJson });
      if (typeof window !== 'undefined' && window.footerContent) {
        window.footerContent.footerData = data;
        window.footerContent._footerLinkMode = data.linkSections?.length > 1 ? 'static' : 'menu';
      }
      window.dispatchEvent(new CustomEvent('glow-footer-config-saved', { detail: { workplaceId: wid } }));
      setSaveMessage('저장되었습니다. 푸터에 반영되었습니다.');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '저장 중 오류가 발생했습니다.';
      setSaveMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  const sectionHeaderStyle = {
    borderLeft: '4px solid var(--theme-color-primary, #e97730)',
    paddingLeft: '12px',
    marginBottom: '0.75rem',
    fontSize: '1rem',
    fontWeight: '600'
  };

  // Footer 테마 섹션 표시 여부 (true로 변경 시 상단 테마 선택·저장 카드 노출)
  const showFooterThemeSection = false;

  return (
    <div className="container-fluid footer-area-manager-page">
      <style>{FOOTER_AREA_MANAGER_PAGE_CSS}</style>
      {/* 상단: 테마 선택 & 저장 (숨김) */}
      {showFooterThemeSection && (
        <div className="card shadow-sm mb-3">
          <div className="card-body py-3">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <label className="mb-0 fw-semibold">Footer 테마</label>
              <CustomSelect
                value={FOOTER_THEME_OPTIONS.find((o) => o.value === footerTheme) || FOOTER_THEME_OPTIONS[0]}
                onChange={handleFooterThemeChange}
                options={FOOTER_THEME_OPTIONS}
                isClearable={false}
                style={{ minWidth: '140px' }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
              {saveMessage && (
                <span className="text-info small">{saveMessage}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="alert alert-secondary py-2 mb-3" role="status">
          workplace_footer_config 에서 푸터 설정을 불러오는 중…
        </div>
      )}
      {loadError && (
        <div className="alert alert-danger py-2 mb-3" role="alert">
          <strong>DB 조회 오류:</strong> {loadError} 아래는 빈 편집 화면입니다. 저장 시 새 설정이 기록될 수 있습니다.
        </div>
      )}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <span className="text-muted small">
          데이터 소스: <code>workplace_footer_config</code> (workplaceId={workplaceId})
        </span>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || loading}>
            {saving ? '저장 중...' : '전체 저장'}
          </button>
          {saveMessage && <span className="text-info small">{saveMessage}</span>}
        </div>
      </div>

      {/* GLOW Platform 섹션 */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0" style={sectionHeaderStyle}>GLOW Platform 섹션</h6>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
            {data.glowPlatform && (
              <div className="row g-3">
                {/* 미리보기 뷰어 (좌측) - 실제 footer-theme-glow 디자인과 동일 */}
                <div className="col-12 col-lg-4">
                  <div className="small text-muted mb-1">미리보기</div>
                  <div
                    className="rounded-3 p-3 overflow-auto footer-area footer-theme-glow"
                    style={{
                      backgroundColor: resolveGlowFooterPreviewBackground(data.glowPlatform),
                      color: '#fff',
                      minHeight: '320px',
                      maxHeight: '420px'
                    }}
                  >
                    {/* 로고 + 제목/부제목 (footer-glow-platform-logo-container) */}
                    {data.glowPlatform.logo && (
                      <div className="d-flex gap-2 align-items-center mb-3" style={{ gap: '16px', marginBottom: '20px' }}>
                        <div
                          className="rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center fw-bold text-white"
                          style={{
                            width: `${Math.min(data.glowPlatform.logo.size || 49, 49)}px`,
                            height: `${Math.min(data.glowPlatform.logo.size || 49, 49)}px`,
                            backgroundColor: data.glowPlatform.logo.backgroundColor || 'rgba(255,255,255,0.08)',
                            border: data.glowPlatform.logo.border || '1px solid rgba(255,255,255,0.2)',
                            fontSize: data.glowPlatform.logo.fontSize || '1.5rem'
                          }}
                        >
                          {data.glowPlatform.logo.letter || ''}
                        </div>
                        <div className="d-flex flex-column justify-content-center">
                          <h3 className="mb-0 text-white fw-bold" style={{ fontSize: '20px', lineHeight: 1.2 }}>
                            {data.glowPlatform.title || ''}
                          </h3>
                          <p className="mb-0 text-secondary" style={{ fontSize: '14px', lineHeight: 1.5 }}>
                            {data.glowPlatform.subtitle || ''}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* 설명 (footer-glow-platform-description) */}
                    {data.glowPlatform.description && (
                      <p
                        className="mb-3"
                        style={{ color: '#ccc', fontSize: '14px', lineHeight: 1.7, marginBottom: '20px' }}
                      >
                        {data.glowPlatform.description}
                      </p>
                    )}
                    {/* 배지 (실제 푸터와 동일: footer-glow-platform-badges-container / footer-glow-platform-badge, border 반영) */}
                    {(data.glowPlatform.badges || []).length > 0 && (
                      <div className="footer-glow-platform-badges-container">
                        {(data.glowPlatform.badges || []).map((b, i) => (
                          <div
                            key={i}
                            className="footer-glow-platform-badge"
                            style={{
                              backgroundColor: b.backgroundColor || '#065f46',
                              color: b.color || '#05df72',
                              border: b.borderColor ? `1px solid ${b.borderColor}` : '1px solid #05df72'
                            }}
                          >
                            {b.svgKey ? (
                              <span className="footer-glow-platform-badge-icon">
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path><circle cx="12" cy="8" r="6"></circle></svg>'
                                  }}
                                />
                              </span>
                            ) : (
                              <span
                                className="footer-glow-platform-badge-dot"
                                style={{
                                  backgroundColor: b.dotColor || '#4ade80'
                                }}
                              />
                            )}
                            <span>{b.text || ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* 배지 하단 로고 (실제 푸터와 동일 — 텍스트 라벨 없이 이미지만) */}
                    {(data.glowPlatform.partnerLogos || []).length > 0 && (
                      <div className="mt-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="footer-glow-platform-partner-logos-shell">
                          <div className="footer-glow-platform-partner-logos">
                            {(data.glowPlatform.partnerLogos || []).map((pl, pi) => (
                              <div key={pi} className="footer-glow-platform-partner-logo-item">
                                {pl.imageUrl?.trim() && shouldLoadFooterPartnerLogoImageUrl(pl.imageUrl) ? (
                                  <img
                                    className="footer-glow-platform-partner-logo-img"
                                    src={pl.imageUrl.trim()}
                                    alt={pl.alt != null && String(pl.alt).trim() !== '' ? String(pl.alt) : ''}
                                    style={{ maxWidth: '100%', maxHeight: 28, objectFit: 'contain' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : pl.imageUrl?.trim() ? (
                                  <span className="text-muted" style={{ fontSize: 9 }} title="경로를 완성하면 미리보기됩니다">
                                    …
                                  </span>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: 9 }}>
                                    URL
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* 편집 폼 (우측) */}
                <div className="col-12 col-lg-8">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small fw-semibold mb-1">푸터 배경색 (GLOW 테마)</label>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <input
                      type="color"
                      className="form-control form-control-sm form-control-color p-1"
                      style={{ width: '2.75rem', height: '1.85rem', cursor: 'pointer' }}
                      value={
                        /^#[0-9A-Fa-f]{6}$/.test(data.glowPlatform?.themeBackgroundColor || '')
                          ? data.glowPlatform.themeBackgroundColor
                          : GLOW_FOOTER_DEFAULT_THEME_BG
                      }
                      onChange={(e) => updateData('glowPlatform.themeBackgroundColor', e.target.value)}
                      title="푸터 배경색"
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ maxWidth: '11rem' }}
                      placeholder={GLOW_FOOTER_DEFAULT_THEME_BG}
                      value={data.glowPlatform?.themeBackgroundColor ?? ''}
                      onChange={(e) => updateData('glowPlatform.themeBackgroundColor', e.target.value)}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => updateData('glowPlatform.themeBackgroundColor', '')}
                    >
                      CSS 기본값
                    </button>
                  </div>
                  <div className="form-text small text-muted">
                    비우면 실제 사이트는 CSS 기본색({GLOW_FOOTER_DEFAULT_THEME_BG})을 씁니다. 저장 후 푸터에 반영됩니다.
                  </div>
                </div>
                {/* 로고 글자 ~ 로고 테두리: 한 줄 배치 */}
                <div className="col-12">
                  <div className="d-flex flex-wrap align-items-end gap-4">
                    <div>
                      <label className="form-label small fw-semibold mb-1">로고 글자</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: '3.5rem' }}
                        value={data.glowPlatform.logo?.letter ?? 'G'}
                        onChange={(e) => updateData('glowPlatform.logo.letter', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="form-label small fw-semibold mb-1">로고 크기(px)</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: '4rem' }}
                        value={data.glowPlatform.logo?.size ?? 49}
                        onChange={(e) => updateData('glowPlatform.logo.size', Number(e.target.value) || 49)}
                      />
                    </div>
                    <div>
                      <label className="form-label small fw-semibold mb-1">로고 배경색</label>
                      <input
                        type="color"
                        className="form-control form-control-sm form-control-color p-1"
                        style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                        value={/^#[0-9A-Fa-f]{6}$/.test(data.glowPlatform.logo?.backgroundColor) ? data.glowPlatform.logo.backgroundColor : '#9333ea'}
                        onChange={(e) => updateData('glowPlatform.logo.backgroundColor', e.target.value)}
                        title="로고 배경색"
                      />
                    </div>
                    <div className="d-flex align-items-end gap-1">
                      <div>
                        <label className="form-label small fw-semibold mb-1">테두리</label>
                        <div className="d-flex gap-1 align-items-center">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: '3rem' }}
                            min={0}
                            value={(() => {
                              const m = (data.glowPlatform.logo?.border || '').match(/^(\d+)px/);
                              return m ? m[1] : '1';
                            })()}
                            onChange={(e) => {
                              const w = e.target.value ? `${e.target.value}px` : '1px';
                              const rest = (data.glowPlatform.logo?.border || '1px solid #ffffff33').replace(/^\d+px\s*/, '') || 'solid #ffffff33';
                              updateData('glowPlatform.logo.border', `${w} ${rest}`);
                            }}
                            title="두께(px)"
                          />
                          <CustomSelect
                            value={(() => {
                              const m = (data.glowPlatform.logo?.border || '').match(/\b(solid|dashed|none)\b/);
                              return m ? m[1] : 'solid';
                            })()}
                            onChange={(style) => {
                              const m = (data.glowPlatform.logo?.border || '1px solid #ffffff33').match(/^(\d+px)\s+\S+\s+(.+)$/);
                              const width = m ? m[1] : '1px';
                              const color = m ? m[2] : '#ffffff33';
                              updateData('glowPlatform.logo.border', `${width} ${style} ${color}`);
                            }}
                            options={BORDER_STYLE_OPTIONS}
                            isClearable={false}
                            isSearchable={false}
                            style={{ width: '9rem', minWidth: '9rem' }}
                            title="스타일"
                          />
                          <input
                            type="color"
                            className="form-control form-control-sm form-control-color p-1"
                            style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                            value={(() => {
                              const m = (data.glowPlatform.logo?.border || '').match(/#([0-9A-Fa-f]{6})/);
                              return m ? `#${m[1]}` : '#ffffff';
                            })()}
                            onChange={(e) => {
                              const color = e.target.value;
                              const m = (data.glowPlatform.logo?.border || '1px solid #ffffff33').match(/^(\d+px)\s+(\S+)\s+/);
                              const width = m ? m[1] : '1px';
                              const style = m ? m[2] : 'solid';
                              updateData('glowPlatform.logo.border', `${width} ${style} ${color}`);
                            }}
                            title="테두리 색상"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">제목</label>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="한글"
                        value={data.glowPlatform.title ?? ''}
                        onChange={(e) => updateData('glowPlatform.title', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="영문"
                        value={data.glowPlatform.titleEn ?? ''}
                        onChange={(e) => updateData('glowPlatform.titleEn', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">부제목</label>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="한글"
                        value={data.glowPlatform.subtitle ?? ''}
                        onChange={(e) => updateData('glowPlatform.subtitle', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="영문"
                        value={data.glowPlatform.subtitleEn ?? ''}
                        onChange={(e) => updateData('glowPlatform.subtitleEn', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">설명</label>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        placeholder="한글"
                        value={data.glowPlatform.description ?? ''}
                        onChange={(e) => updateData('glowPlatform.description', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        placeholder="영문"
                        value={data.glowPlatform.descriptionEn ?? ''}
                        onChange={(e) => updateData('glowPlatform.descriptionEn', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold mb-2">배지 (Badges)</label>
                  {(data.glowPlatform.badges || []).map((badge, bi) => (
                    <div key={bi} className="card mb-2">
                      <div className="card-body py-2">
                        {/* 1행: 배지명 + 삭제(문의·링크 섹션과 동일하게 flex 우측 정렬) */}
                        <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
                          <div className="flex-grow-1" style={{ minWidth: 0, flex: '1 1 10rem' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="배지명 (한글)"
                              value={badge.text ?? ''}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.badges[bi]) next.glowPlatform.badges[bi].text = e.target.value;
                                  return next;
                                });
                              }}
                            />
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0, flex: '1 1 10rem' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="배지명 (영문)"
                              value={badge.textEn ?? ''}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.badges[bi]) next.glowPlatform.badges[bi].textEn = e.target.value;
                                  return next;
                                });
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm btn-icon-square flex-shrink-0"
                            onClick={() => removeBadge(bi)}
                            title="행 삭제"
                            aria-label="삭제"
                          >
                            <DeleteIconX />
                          </button>
                        </div>
                        {/* 2행: 배경 / 테두리·글자 / 점 3컬럼 (모바일 포함) */}
                        <div className="row g-2 align-items-center">
                          <div className="col-4">
                            <label className="form-label small text-muted mb-0">배경</label>
                            <input
                              type="color"
                              className="form-control form-control-sm form-control-color p-1 d-block"
                              style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                              value={/^#[0-9A-Fa-f]{6}$/.test(badge.backgroundColor) ? badge.backgroundColor : '#065f46'}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.badges[bi]) next.glowPlatform.badges[bi].backgroundColor = e.target.value;
                                  return next;
                                });
                              }}
                              title="배경색"
                            />
                          </div>
                          <div className="col-4">
                            <label className="form-label small text-muted mb-0">테두리/글자</label>
                            <input
                              type="color"
                              className="form-control form-control-sm form-control-color p-1 d-block"
                              style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                              value={/^#[0-9A-Fa-f]{6}$/.test(badge.borderColor) ? badge.borderColor : '#05df72'}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.badges[bi]) {
                                    next.glowPlatform.badges[bi].borderColor = e.target.value;
                                    next.glowPlatform.badges[bi].color = e.target.value;
                                  }
                                  return next;
                                });
                              }}
                              title="테두리·글자색"
                            />
                          </div>
                          <div className="col-4">
                            <label className="form-label small text-muted mb-0">점</label>
                            <input
                              type="color"
                              className="form-control form-control-sm form-control-color p-1 d-block"
                              style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                              value={/^#[0-9A-Fa-f]{6}$/.test(badge.dotColor) ? badge.dotColor : '#4ade80'}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.badges[bi]) next.glowPlatform.badges[bi].dotColor = e.target.value;
                                  return next;
                                });
                              }}
                              title="점(dot) 색상"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="d-flex justify-content-end mt-2">
                    <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={addBadge} title="항목 추가" aria-label="항목 추가"><AddIconPlus /></button>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold mb-2">배지 하단 로고</label>
                  <p className="small text-muted mb-2">
                    이미지 URL을 입력하세요. (예: <code>/assets/...</code> 또는 https://…) 링크가 있으면 클릭 시{' '}
                    <strong>항상 새 창</strong>으로 열립니다. 대체 텍스트는 접근성용입니다.
                  </p>
                  {(data.glowPlatform.partnerLogos || []).map((pl, pi) => (
                    <div key={pi} className="card mb-2">
                      <div className="card-body py-2">
                        <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
                          <div className="flex-grow-1" style={{ minWidth: 0, flex: '1 1 14rem' }}>
                            <label className="form-label small text-muted mb-0">이미지 URL</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="https://… 또는 /upload/…"
                              value={pl.imageUrl ?? ''}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.partnerLogos[pi]) {
                                    next.glowPlatform.partnerLogos[pi].imageUrl = e.target.value;
                                  }
                                  return next;
                                });
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm btn-icon-square flex-shrink-0"
                            onClick={() => removePartnerLogo(pi)}
                            title="행 삭제"
                            aria-label="삭제"
                          >
                            <DeleteIconX />
                          </button>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">대체 텍스트 (한글)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="접근성 · SEO"
                              value={pl.alt ?? ''}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.partnerLogos[pi]) {
                                    next.glowPlatform.partnerLogos[pi].alt = e.target.value;
                                  }
                                  return next;
                                });
                              }}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">대체 텍스트 (영문)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={pl.altEn ?? ''}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.partnerLogos[pi]) {
                                    next.glowPlatform.partnerLogos[pi].altEn = e.target.value;
                                  }
                                  return next;
                                });
                              }}
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label small text-muted mb-0">클릭 시 이동 URL (선택 · 새 창)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="비우면 링크 없음 · 입력 시 새 창으로 열림"
                              value={pl.link ?? ''}
                              onChange={(e) => {
                                setData((prev) => {
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (next.glowPlatform.partnerLogos[pi]) {
                                    next.glowPlatform.partnerLogos[pi].link = e.target.value;
                                  }
                                  return next;
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="d-flex justify-content-end mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm btn-icon-square"
                      onClick={addPartnerLogo}
                      title="로고 항목 추가"
                      aria-label="로고 항목 추가"
                    >
                      <AddIconPlus />
                    </button>
                  </div>
                </div>
              </div>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* 링크 섹션 - 섹션별로 미리보기(좌) + 편집 폼(우) */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0" style={sectionHeaderStyle}>링크 섹션</h6>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          {(data.linkSections || []).length === 0 ? (
            <>
              <div className="text-secondary small">링크 섹션이 없습니다.</div>
              <div className="d-flex justify-content-end mt-2">
                <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={addLinkSection} title="링크 섹션 추가" aria-label="링크 섹션 추가"><AddIconPlus /></button>
              </div>
            </>
          ) : (
            <>
              {(data.linkSections || []).map((section, sectionIndex) => (
                <div key={sectionIndex} className="row g-3 mb-4">
                  {/* 해당 섹션만 미리보기 (좌측) */}
                  <div className="col-12 col-lg-4">
                    <div className="small text-muted mb-1">미리보기</div>
                    <div
                      className="rounded-3 p-3 footer-area footer-theme-glow"
                      style={{
                        minHeight: '120px',
                        backgroundColor: resolveGlowFooterPreviewBackground(data.glowPlatform)
                      }}
                    >
                      <h4 className="footer-links-section-title">{section.title || ''}</h4>
                      <ul className="footer-links-section-list">
                        {[...(section.items || [])]
                          .sort((a, b) => (Number(a.sortOrder) || 1e6) - (Number(b.sortOrder) || 1e6))
                          .map((item, ii) => (
                          <li key={ii} className="footer-links-section-list-item">
                            <span className="footer-links-section-link d-flex align-items-center">
                              {section.iconType === 'external' ? (
                                <span className="me-2">↗</span>
                              ) : (
                                <span className="me-2">→</span>
                              )}
                              {item.text || ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {(section.items || []).length === 0 && (
                        <div className="text-secondary small">링크가 없습니다.</div>
                      )}
                    </div>
                  </div>
                  {/* 편집 폼 (우측) */}
                  <div className="col-12 col-lg-8">
                    <div className="card">
                      <div className="card-header py-2">
                        <div className="d-flex flex-nowrap align-items-end gap-2">
                          <div className="flex-shrink-0" style={{ width: '4.75rem' }}>
                            <label className="form-label small text-muted mb-0">순서</label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={section.sortOrder ?? ''}
                              placeholder={String(sectionIndex + 1)}
                              onChange={(e) => {
                                const v = e.target.value;
                                setLinkSection(sectionIndex, 'sortOrder', v === '' ? undefined : Number(v));
                              }}
                            />
                          </div>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ minWidth: 0, flex: '1 1 0' }}
                            value={section.title ?? ''}
                            onChange={(e) => setLinkSection(sectionIndex, 'title', e.target.value)}
                            placeholder="섹션 제목 (한글)"
                          />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ minWidth: 0, flex: '1 1 0' }}
                            value={section.titleEn ?? ''}
                            onChange={(e) => setLinkSection(sectionIndex, 'titleEn', e.target.value)}
                            placeholder="섹션 제목 (영문)"
                          />
                          <CustomSelect
                            value={section.iconType ?? 'arrow'}
                            onChange={(v) => setLinkSection(sectionIndex, 'iconType', v)}
                            options={LINK_SECTION_ICON_OPTIONS}
                            isClearable={false}
                            isSearchable={false}
                            style={{ width: '100px', minWidth: '100px', flexShrink: 0 }}
                          />
                          <button type="button" className="btn btn-outline-danger btn-sm btn-icon-square flex-shrink-0" onClick={() => removeLinkSection(sectionIndex)} title="섹션 삭제" aria-label="섹션 삭제"><DeleteIconX /></button>
                        </div>
                      </div>
                      <div className="card-body py-2">
                        <div className="footer-link-items-group">
                        {(section.items || []).map((item, itemIndex) => (
                          <React.Fragment key={itemIndex}>
                            {itemIndex > 0 ? <hr className="footer-link-item-divider" aria-hidden="true" /> : null}
                            <div className="card border-0 mb-0 position-relative footer-link-item-editor">
                            <div className="card-body py-2 pe-5">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm btn-icon-square position-absolute top-0 end-0 mt-2"
                                style={{ zIndex: 1 }}
                                onClick={() => removeLinkItem(sectionIndex, itemIndex)}
                                title="삭제"
                                aria-label="삭제"
                              >
                                <DeleteIconX />
                              </button>
                              <div className="row g-2 mb-1">
                                <div className="col-6 col-lg-2">
                                  <label className="form-label small text-muted mb-0">순서</label>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={item.sortOrder ?? ''}
                                    placeholder={String(itemIndex + 1)}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setLinkItem(sectionIndex, itemIndex, 'sortOrder', v === '' ? undefined : Number(v));
                                    }}
                                  />
                                </div>
                                <div className="col-6 col-lg-5">
                                  <label className="form-label small text-muted mb-0">링크 텍스트 (한글)</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="링크 텍스트 (한글)"
                                    value={item.text ?? ''}
                                    onChange={(e) => setLinkItem(sectionIndex, itemIndex, 'text', e.target.value)}
                                  />
                                </div>
                                <div className="col-12 col-lg-5">
                                  <label className="form-label small text-muted mb-0">링크 텍스트 (영문)</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="링크 텍스트 (영문)"
                                    value={item.textEn ?? ''}
                                    onChange={(e) => setLinkItem(sectionIndex, itemIndex, 'textEn', e.target.value)}
                                  />
                                </div>
                              </div>
                              <label className="form-label small text-muted mb-0">링크 URL</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="링크 URL"
                                value={item.link ?? ''}
                                onChange={(e) => setLinkItem(sectionIndex, itemIndex, 'link', e.target.value)}
                              />
                              <div className="form-check mt-2 mb-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`footer-link-open-new-tab-${sectionIndex}-${itemIndex}`}
                                  checked={!!item.openInNewTab}
                                  onChange={(e) => setLinkItem(sectionIndex, itemIndex, 'openInNewTab', e.target.checked)}
                                />
                                <label
                                  className="form-check-label small text-muted"
                                  htmlFor={`footer-link-open-new-tab-${sectionIndex}-${itemIndex}`}
                                >
                                  새 탭에서 열기
                                </label>
                              </div>
                            </div>
                          </div>
                          </React.Fragment>
                        ))}
                        </div>
                        <div className="d-flex justify-content-end mt-2">
                          <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={() => addLinkItem(sectionIndex)} title="링크 추가" aria-label="링크 추가"><AddIconPlus /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="d-flex justify-content-end mt-2">
                <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={addLinkSection} title="링크 섹션 추가" aria-label="링크 섹션 추가"><AddIconPlus /></button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 문의 & 지원 */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0" style={sectionHeaderStyle}>문의 & 지원</h6>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* 미리보기 뷰어 (좌측) - footer-theme-glow 문의 & 지원과 동일 */}
            <div className="col-12 col-lg-4">
              <div className="small text-muted mb-1">미리보기</div>
              <div
                className="rounded-3 p-3 overflow-auto footer-area footer-theme-glow"
                style={{
                  minHeight: '220px',
                  maxHeight: '360px',
                  backgroundColor: resolveGlowFooterPreviewBackground(data.glowPlatform)
                }}
              >
                <div className="footer-contact-container">
                  <h4 className="footer-contact-title">{data.contact?.title || ''}</h4>
                  <ul className="footer-contact-list">
                    {(data.contact?.items || []).map((item, idx) => (
                      <li key={idx} className="footer-contact-list-item">
                        <span className="footer-contact-icon" style={{ marginTop: item.type === 'address' ? '2px' : 0 }}>
                          {item.type === 'other' && item.iconSvg ? (
                            <span style={{ color: '#51a2ff', display: 'inline-flex', alignItems: 'center' }} dangerouslySetInnerHTML={{ __html: item.iconSvg }} />
                          ) : (
                            CONTACT_PREVIEW_ICONS[item.type] || CONTACT_PREVIEW_ICONS.phone
                          )}
                        </span>
                        {item.type === 'address' ? (
                          <div className="footer-contact-address-container">
                            {item.title && <div className="footer-contact-address-title">{item.title}</div>}
                            <div>{item.content || ''}</div>
                          </div>
                        ) : item.type === 'other' ? (
                          <div className="footer-contact-address-container">
                            {item.title && <div className="footer-contact-address-title">{item.title}</div>}
                            <div className="footer-contact-content">{item.content || ''}</div>
                          </div>
                        ) : (
                          <div className="footer-contact-content">{item.content || ''}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                {(!data.contact?.items || data.contact.items.length === 0) && (
                  <div className="text-secondary small">문의 항목이 없습니다.</div>
                )}
              </div>
            </div>
            {/* 편집 폼 (우측) */}
            <div className="col-12 col-lg-8">
            <div className="mb-2 row g-2">
              <div className="col-6">
                <label className="form-label small text-muted mb-0">섹션 제목 (한글)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="섹션 제목 (한글)"
                  value={data.contact?.title ?? ''}
                  onChange={(e) => setData((prev) => ({ ...prev, contact: { ...prev.contact, title: e.target.value } }))}
                />
              </div>
              <div className="col-6">
                <label className="form-label small text-muted mb-0">섹션 제목 (영문)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="섹션 제목 (영문)"
                  value={data.contact?.titleEn ?? ''}
                  onChange={(e) => setData((prev) => ({ ...prev, contact: { ...prev.contact, titleEn: e.target.value } }))}
                />
              </div>
            </div>
            {(data.contact?.items || []).map((item, idx) => (
              <div key={idx} className="card mb-2">
                <div className="card-body py-2">
                  {/* 1행: 콤보 + X 버튼 */}
                  <label className="form-label small text-muted mb-0">아이콘/타입</label>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <CustomSelect
                      value={item.iconType ?? item.type ?? 'phone'}
                      onChange={(v) => {
                        setContactItem(idx, 'iconType', v);
                        setContactItem(idx, 'type', v === 'E-mail' ? 'E-mail' : v);
                        if (v === 'other' && (item.iconSvg === undefined || item.iconSvg === null)) setContactItem(idx, 'iconSvg', '');
                      }}
                      options={CONTACT_ICON_TYPES}
                      isClearable={false}
                      isSearchable={false}
                      style={{ minWidth: '200px' }}
                      className="contact-item-type-select"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm btn-icon-square ms-auto flex-shrink-0"
                      onClick={() => removeContactItem(idx)}
                      title="삭제"
                      aria-label="삭제"
                    >
                      <DeleteIconX />
                    </button>
                  </div>
                  {/* 2행: 주소는 제목만; 전화·이메일·운영시간은 DB에 title 없이 content만 있는 경우가 많아 표시 시 title||content 사용 (빈 문자열은 ??로는 폴백 안 됨) */}
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label className="form-label small text-muted mb-0">
                        {item.type === 'phone' || item.type === 'E-mail' || item.type === 'hours'
                          ? '내용 (한글)'
                          : '제목 (한글)'}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={
                          item.type === 'address' ? '제목 (예: 한국외국어대학교 G-RISE 사업단)' :
                          item.type === 'E-mail' ? '이메일 주소' :
                          item.type === 'hours' ? '운영시간' :
                          item.type === 'phone' ? '전화번호' : '제목 (한글)'
                        }
                        value={
                          item.type === 'phone' || item.type === 'E-mail' || item.type === 'hours'
                            ? (item.title || item.content || '')
                            : (item.title ?? '')
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          const syncContent = item.type === 'phone' || item.type === 'E-mail' || item.type === 'hours';
                          setData((prev) => {
                            const next = JSON.parse(JSON.stringify(prev));
                            if (!next.contact?.items?.[idx]) return prev;
                            next.contact.items[idx].title = v;
                            if (syncContent) next.contact.items[idx].content = v;
                            return next;
                          });
                        }}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted mb-0">
                        {item.type === 'phone' || item.type === 'E-mail' || item.type === 'hours'
                          ? '내용 (영문)'
                          : '제목 (영문)'}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={
                          item.type === 'E-mail' ? '이메일 주소 (영문)' :
                          item.type === 'hours' ? '운영시간 (영문)' :
                          item.type === 'phone' ? '전화번호 (영문)' : '제목 (영문)'
                        }
                        value={
                          item.type === 'phone' || item.type === 'E-mail' || item.type === 'hours'
                            ? (item.titleEn || item.contentEn || '')
                            : (item.titleEn ?? '')
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          const syncContent = item.type === 'phone' || item.type === 'E-mail' || item.type === 'hours';
                          setData((prev) => {
                            const next = JSON.parse(JSON.stringify(prev));
                            if (!next.contact?.items?.[idx]) return prev;
                            next.contact.items[idx].titleEn = v;
                            if (syncContent) next.contact.items[idx].contentEn = v;
                            return next;
                          });
                        }}
                      />
                    </div>
                  </div>
                  {/* 기타 타입만: 아이콘 SVG */}
                  {item.type === 'other' && (
                    <div className="mb-2">
                      <label className="form-label small text-muted mb-0">아이콘 SVG 태그</label>
                      <textarea
                        className="form-control form-control-sm font-monospace"
                        placeholder="<svg xmlns='...' ...>...</svg>"
                        rows={3}
                        value={item.iconSvg ?? ''}
                        onChange={(e) => setContactItem(idx, 'iconSvg', e.target.value)}
                      />
                    </div>
                  )}
                  {/* 3행: 주소/기타만 상세 텍스트 영역 */}
                  {(item.type === 'address' || item.type === 'other') && (
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label small text-muted mb-0">{item.type === 'other' ? '내용 (한글)' : '주소 상세 (한글)'}</label>
                        {item.type === 'other' ? (
                          <textarea
                            className="form-control form-control-sm"
                            placeholder="세부 내용을 입력하세요"
                            rows={3}
                            value={item.content ?? ''}
                            onChange={(e) => setContactItem(idx, 'content', e.target.value)}
                          />
                        ) : (
                          <textarea
                            className="form-control form-control-sm"
                            placeholder="주소 상세 (도로명, 건물명, 동호수 등)"
                            rows={3}
                            value={item.content ?? ''}
                            onChange={(e) => setContactItem(idx, 'content', e.target.value)}
                          />
                        )}
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted mb-0">{item.type === 'other' ? '내용 (영문)' : '주소 상세 (영문)'}</label>
                        <textarea
                          className="form-control form-control-sm"
                          placeholder={item.type === 'other' ? '세부내용 (영문)' : '주소 상세 (영문)'}
                          rows={3}
                          value={item.contentEn ?? ''}
                          onChange={(e) => setContactItem(idx, 'contentEn', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="d-flex justify-content-end mt-2">
              <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={addContactItem} title="문의 항목 추가" aria-label="문의 항목 추가"><AddIconPlus /></button>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* 카피라이트·하단 영역 - 항목별 미리보기(좌) + 편집(우) */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0" style={sectionHeaderStyle}>카피라이트·하단 영역</h6>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          {/* Copyright 문구: 미리보기(좌) + 편집 한글/영문(우) - 구조: copyright.copyright.{ kor, eng } */}
          <div className="row g-3 align-items-start mb-4">
            <div className="col-12 col-lg-4">
              <div className="small text-muted mb-1">미리보기</div>
              <div
                className="rounded-3 overflow-hidden footer-area footer-theme-glow"
                style={{
                  minHeight: '52px',
                  backgroundColor: resolveGlowFooterPreviewBackground(data.glowPlatform)
                }}
              >
                <div className="footer-copyright-area">
                  <div className="container py-2">
                    <span className="footer-copyright-text">{data.copyright?.copyright?.kor ?? ''}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-8">
              <label className="form-label small fw-semibold mb-1">Copyright 문구</label>
              <div className="row g-2">
                <div className="col-6">
                  <textarea
                    rows={3}
                    className="form-control form-control-sm"
                    placeholder="Copyright 문구 (한글)"
                    value={data.copyright?.copyright?.kor ?? ''}
                    onChange={(e) => setData((prev) => {
                      const next = { ...prev, copyright: { ...prev.copyright } };
                      next.copyright.copyright = { ...(prev.copyright?.copyright || {}), kor: e.target.value };
                      return next;
                    })}
                  />
                </div>
                <div className="col-6">
                  <textarea
                    rows={3}
                    className="form-control form-control-sm"
                    placeholder="Copyright 문구 (영문)"
                    value={data.copyright?.copyright?.eng ?? ''}
                    onChange={(e) => setData((prev) => {
                      const next = { ...prev, copyright: { ...prev.copyright } };
                      next.copyright.copyright = { ...(prev.copyright?.copyright || {}), eng: e.target.value };
                      return next;
                    })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 하단 링크: 미리보기(좌) + 편집(우, 행 추가/삭제) */}
          <div className="row g-3 align-items-start mb-4">
            <div className="col-12 col-lg-4">
              <div className="small text-muted mb-1">미리보기</div>
              <div
                className="rounded-3 overflow-hidden footer-area footer-theme-glow"
                style={{
                  minHeight: '52px',
                  backgroundColor: resolveGlowFooterPreviewBackground(data.glowPlatform)
                }}
              >
                <div className="footer-copyright-area">
                  <div className="container py-2">
                    <span className="footer-copyright-text">
                      {(data.copyright?.links ?? []).map((item, i) => (
                        <span key={i} className="footer-copyright-link" style={{ marginRight: '10px', cursor: 'default' }}>
                          {item.kor || item.eng || ''}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-8">
              <label className="form-label small fw-semibold mb-1">하단 링크 (행 추가/삭제)</label>
              {(data.copyright?.links ?? []).map((item, li) => (
                <div key={li} className="card mb-2">
                  <div className="card-body py-2">
                    {/* 1행: 삭제 버튼 (문의지원 섹션과 동일 레이아웃) */}
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="small text-muted">항목 {li + 1}</span>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm btn-icon-square ms-auto flex-shrink-0"
                        onClick={() => removeFooterBottomLink(li)}
                        title="행 삭제"
                        aria-label="행 삭제"
                      >
                        <DeleteIconX />
                      </button>
                    </div>
                    {/* 2행: 링크 텍스트 (한글/영문) */}
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small text-muted mb-0">링크 텍스트 (한글)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="링크 텍스트 (한글)"
                          value={item.kor ?? ''}
                          onChange={(e) => setFooterBottomLinkItem(li, 'kor', e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted mb-0">링크 텍스트 (영문)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="링크 텍스트 (영문)"
                          value={item.eng ?? ''}
                          onChange={(e) => setFooterBottomLinkItem(li, 'eng', e.target.value)}
                        />
                      </div>
                    </div>
                    {/* 3행: 링크 URL */}
                    <label className="form-label small text-muted mb-0">링크 URL</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="/privacy, /terms, /sitemap 등"
                      value={item.link ?? ''}
                      onChange={(e) => setFooterBottomLinkItem(li, 'link', e.target.value)}
                    />
                  </div>
                </div>
              ))}
              <div className="d-flex justify-content-end mt-2">
                <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={addFooterBottomLink} title="항목 추가" aria-label="항목 추가"><AddIconPlus /></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 협력 기관 (메인 레이아웃 캐러셀) — 카피라이트·하단 영역 카드와 형제 */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0" style={sectionHeaderStyle}>협력 기관 (메인 레이아웃 하단 캐러셀)</h6>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSaveRelatedSites}
            disabled={relatedSitesSaving || relatedSitesLoading}
          >
            {relatedSitesSaving ? '저장 중...' : '협력 기관 저장'}
          </button>
          {relatedSitesSaveMessage && <span className="text-info small">{relatedSitesSaveMessage}</span>}
        </div>
      </div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <p className="text-muted small mb-3">
            데이터 소스: <code>workplace_related_sites_config</code> (workplaceId={workplaceId}) · 공개 조회:{' '}
            <code>/api/comm/related-sites/workspace/{workplaceId}/latest</code>
          </p>
          {relatedSitesLoading && (
            <div className="alert alert-secondary py-2 mb-3" role="status">
              협력 기관 설정을 불러오는 중…
            </div>
          )}
          {relatedSitesLoadError && (
            <div className="alert alert-warning py-2 mb-3" role="alert">
              {relatedSitesLoadError}
            </div>
          )}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold mb-0">섹션 제목 (한글)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={relatedSites.title ?? ''}
                onChange={(e) => setRelatedSites((p) => ({ ...p, title: e.target.value }))}
                placeholder="협력 기관"
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold mb-0">섹션 제목 (영문)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={relatedSites.titleEn ?? ''}
                onChange={(e) => setRelatedSites((p) => ({ ...p, titleEn: e.target.value }))}
                placeholder="Partner Organizations"
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small fw-semibold mb-0">자동 전환(ms)</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min={500}
                step={100}
                value={relatedSites.interval ?? 3000}
                onChange={(e) => setRelatedSites((p) => ({ ...p, interval: Number(e.target.value) || 3000 }))}
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small fw-semibold mb-0">한 화면 개수</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min={1}
                max={20}
                value={relatedSites.itemsPerView ?? 5}
                onChange={(e) => setRelatedSites((p) => ({ ...p, itemsPerView: Number(e.target.value) || 5 }))}
              />
            </div>
            <div className="col-12 col-md-2 d-flex align-items-end">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="related-sites-autoplay"
                  checked={relatedSites.autoPlay === true}
                  onChange={(e) => setRelatedSites((p) => ({ ...p, autoPlay: e.target.checked }))}
                />
                <label className="form-check-label small" htmlFor="related-sites-autoplay">
                  자동 재생
                </label>
              </div>
            </div>
          </div>
          <label className="form-label small fw-semibold mb-2">기관 목록 (로고 URL · 링크 · 이름)</label>
          {(relatedSites.sites || []).map((site, si) => (
            <div key={si} className="card mb-2 border">
              <div className="card-body py-2">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="small text-muted">항목 {si + 1}</span>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm btn-icon-square ms-auto"
                    onClick={() => removeRelatedSiteRow(si)}
                    title="삭제"
                    aria-label="삭제"
                  >
                    <DeleteIconX />
                  </button>
                </div>
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label small text-muted mb-0">로고 이미지 경로</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="/assets/images/partners/example.png"
                      value={site.image ?? ''}
                      onChange={(e) => setRelatedSiteField(si, 'image', e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small text-muted mb-0">이동 URL</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="https://"
                      value={site.url ?? ''}
                      onChange={(e) => setRelatedSiteField(si, 'url', e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-muted mb-0">기관명 (한글)</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={site.name ?? ''}
                      onChange={(e) => setRelatedSiteField(si, 'name', e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-muted mb-0">기관명 (영문)</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={site.nameEn ?? ''}
                      onChange={(e) => setRelatedSiteField(si, 'nameEn', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="d-flex justify-content-end mt-2">
            <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={addRelatedSiteRow} title="기관 추가" aria-label="기관 추가">
              <AddIconPlus />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterAreaManager;
export { getDefaultCopyright };
